/**
 * Custom React Query hooks for price data operations
 *
 * These hooks provide type-safe, cached access to price data with
 * automatic background refetching and state management.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { message } from 'antd'
import { priceService } from '@/services/priceService'
import type {
  PriceUpdateFilters,
  PriceFilters,
  HistoryFilters,
} from '../types'

// Query Keys - centralized for cache invalidation
export const PRICE_QUERY_KEYS = {
  pendingUpdates: (filters?: PriceUpdateFilters) => ['price-updates', 'pending', filters] as const,
  currentPrices: (filters?: PriceFilters) => ['prices', 'active', filters] as const,
  history: (filters?: HistoryFilters) => ['price-history', filters] as const,
  health: () => ['price-health'] as const,
  exceptions: () => ['price-exceptions'] as const,
}

/**
 * Fetch pending price updates
 * Staleness: 1 minute (updates frequently during approval workflow)
 */
export function usePendingUpdates(filters?: PriceUpdateFilters) {
  return useQuery({
    queryKey: PRICE_QUERY_KEYS.pendingUpdates(filters),
    queryFn: () => priceService.getPendingUpdates(filters),
    staleTime: 1000 * 60, // 1 minute
    select: (response) => response.data || [],
  })
}

/**
 * Fetch current active prices
 * Staleness: 5 minutes (prices change less frequently)
 */
export function useCurrentPrices(filters?: PriceFilters) {
  return useQuery({
    queryKey: PRICE_QUERY_KEYS.currentPrices(filters),
    queryFn: () => priceService.getCurrentPrices(filters),
    staleTime: 1000 * 60 * 5, // 5 minutes
    select: (response) => ({
      data: response.data || [],
      total: response.meta?.total || 0,
    }),
  })
}

/**
 * Fetch price history
 * Staleness: 1 hour (historical data doesn't change)
 */
export function usePriceHistory(filters?: HistoryFilters) {
  return useQuery({
    queryKey: PRICE_QUERY_KEYS.history(filters),
    queryFn: () => priceService.getPriceHistory(filters),
    staleTime: 1000 * 60 * 60, // 1 hour
    select: (response) => response.data || [],
  })
}

/**
 * Fetch price health metrics
 * Staleness: 5 minutes
 */
export function usePriceHealth() {
  return useQuery({
    queryKey: PRICE_QUERY_KEYS.health(),
    queryFn: () => priceService.getPriceHealthMetrics(),
    staleTime: 1000 * 60 * 5, // 5 minutes
    select: (response) => response.data,
  })
}

/**
 * Fetch price exceptions (items needing review)
 * Staleness: 1 minute
 */
export function usePriceExceptions() {
  return useQuery({
    queryKey: PRICE_QUERY_KEYS.exceptions(),
    queryFn: () => priceService.getExceptions(),
    staleTime: 1000 * 60, // 1 minute
    select: (response) => response.data || [],
  })
}

/**
 * Mutation: Approve price updates
 * Invalidates pending updates and health metrics on success
 */
export function useApprovePrices() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ updateIds, effectiveDate }: { updateIds: string[]; effectiveDate?: string }) =>
      priceService.approvePrices(updateIds, effectiveDate),
    onSuccess: (response) => {
      // Invalidate related queries to trigger refetch
      queryClient.invalidateQueries({ queryKey: ['price-updates'] })
      queryClient.invalidateQueries({ queryKey: ['price-health'] })
      queryClient.invalidateQueries({ queryKey: ['prices'] })

      message.success(`Successfully approved ${response.data?.approved || 0} price update(s)`)
    },
    onError: (error) => {
      message.error(`Failed to approve prices: ${error.message}`)
    },
  })
}

/**
 * Mutation: Reject price updates
 * Invalidates pending updates and health metrics on success
 */
export function useRejectPrices() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ updateIds, reason }: { updateIds: string[]; reason?: string }) =>
      priceService.rejectPrices(updateIds, reason),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['price-updates'] })
      queryClient.invalidateQueries({ queryKey: ['price-health'] })

      message.success(`Successfully rejected ${response.data?.rejected || 0} price update(s)`)
    },
    onError: (error) => {
      message.error(`Failed to reject prices: ${error.message}`)
    },
  })
}

/**
 * Mutation: Rollback price change batch
 * Invalidates history, current prices, and health metrics
 */
export function useRollbackPriceChange() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (historyEntryId: string) => priceService.rollbackPriceChange(historyEntryId),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['price-history'] })
      queryClient.invalidateQueries({ queryKey: ['prices'] })
      queryClient.invalidateQueries({ queryKey: ['price-health'] })

      message.success(`Successfully rolled back ${response.data?.rolledBack || 0} price change(s)`)
    },
    onError: (error) => {
      message.error(`Failed to rollback price changes: ${error.message}`)
    },
  })
}
